import { ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import * as Yup from 'yup';
import { Form, Formik, FormikHelpers } from 'formik';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { FormikError, Input } from '@app/form';
import { Captcha, SubmitButton } from '@app/core';
import { Theme } from '@app/theme';
import { AnalyticsCategoryProvider, Categories } from '@app/analytics';
import { LoginViaCodeModal } from '@app/auth';
import {
  Account,
  API_URL,
  getSafeRedirectPath,
  LOGIN_REDIRECT_PARAM,
  useEmailCheck,
  useLogin,
} from '@app/api';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  h1: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 2,
  },
  joinContainer: {
    ...theme.fonts.label,
    marginTop: theme.marginBase * 3,
    fontWeight: 400,
  },
  join: {
    minWidth: 0,
    paddingLeft: theme.marginBase / 2,
    '&:hover:not(:disabled)': {
      backgroundColor: 'transparent',
    },
  },
  forgottenPasswordLink: {
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
  },
  captcha: {
    marginBottom: theme.marginBase * 2,
  },
  passwordContainer: {
    maxHeight: 200,
    overflow: 'hidden',
    transition: 'max-height .3s, opacity .3s',
    marginBottom: theme.marginBase * 3,
    opacity: 1,
  },
  passwordContainerHidden: {
    maxHeight: 0,
    marginBottom: 0,
    opacity: 0,
  },
  joinContent: {
    padding: 0,
    '& i': {
      fontWeight: 400,
      fontSize: theme.iconSize.small,
    },
  },
  buttonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const emailSchema = Yup.object().shape({
  email: Yup.string().email().required(),
});

const loginSchema = Yup.object().shape({
  email: Yup.string().email().required(),
  password: Yup.string().min(6).required(),
});

interface Props {
  onSuccess?: (account: Account) => any;
  /**
   * Where to go after a successful login. A `?redirect=` query param on the
   * current URL (set when a session expires) takes precedence over it.
   */
  redirectUrl?: string;
  children?: ReactNode;
  title?: ReactNode;
  largeButton?: boolean;
}

interface PropsLocation {
  email: string;
}

export const UniversalLoginForm = ({
  onSuccess,
  redirectUrl,
  children,
  title,
  largeButton,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const location = useLocation();
  const state: PropsLocation = location.state as PropsLocation;
  const [email, setEmail] = useState<string | undefined>(state?.email);
  const initialValues = {
    email: state?.email || '',
    password: '',
    altcha: '',
  };
  const { mutateAsync: login } = useLogin();
  const navigate = useNavigate();

  const [method, setMethod] = useState<
    'PASSWORD' | 'EMAIL' | 'SSO' | undefined
  >();

  const { mutateAsync: checkEmailMutation } = useEmailCheck();
  const onLoginSuccess = (account: Account) => {
    setMethod(undefined);
    if (onSuccess) {
      onSuccess(account);
    }
    const target =
      getSafeRedirectPath(
        new URLSearchParams(location.search).get(LOGIN_REDIRECT_PARAM),
      ) || redirectUrl;
    if (target) {
      navigate(target, { replace: true });
    }
  };

  const checkEmail = async (
    values: typeof initialValues,
    helpers: FormikHelpers<typeof initialValues>,
  ) => {
    const res = await checkEmailMutation({
      email: values.email,
      altcha: values.altcha,
    });
    if (!res.exists) {
      helpers.setFieldError('email', t('form.errors.email.notFound'));
      return;
    }

    if (res.method === 'SSO') {
      window.location = `${API_URL}/connect/oauth2` as any;
      return;
    }
    setEmail(values.email);
    setMethod(res.method);
  };

  const onEmailChange = () => setMethod(undefined);

  const loginViaPassword = async (values: typeof initialValues) => {
    const account = await login(values);
    onLoginSuccess(account);
  };

  const onSubmit = async (
    values: typeof initialValues,
    helpers: FormikHelpers<typeof initialValues>,
  ) => {
    switch (method) {
      case 'PASSWORD':
        return loginViaPassword(values);
      case 'SSO':
      default:
        return checkEmail(values, helpers);
    }
  };

  return (
    <AnalyticsCategoryProvider category={Categories.LOGIN}>
      <Formik
        initialValues={initialValues}
        validationSchema={method === 'PASSWORD' ? loginSchema : emailSchema}
        onSubmit={onSubmit}
      >
        <Form>
          {!!title && <h1 className={classes.h1}>{title}</h1>}
          {children}
          <Input
            name="email"
            label={t('login.email')}
            placeholder="Ex: albert.einstein@gmail.com"
            type="email"
            onChangeEvent={onEmailChange}
          />

          <div
            className={classNames(classes.passwordContainer, {
              [classes.passwordContainerHidden]: method !== 'PASSWORD',
            })}
          >
            <Input
              name="password"
              label={t('login.password')}
              placeholder="****"
              type="password"
              noMargin
              rightAction={
                <Link
                  to="/forgotten-password/request"
                  className={classes.forgottenPasswordLink}
                  target="_blank"
                >
                  <Trans i18nKey="login.forgottenPassword" />
                </Link>
              }
            />
          </div>

          <Captcha className={classes.captcha} invisible />
          <FormikError />

          {largeButton ? (
            <SubmitButton text={t('auth.login.submit')} />
          ) : (
            <div className={classes.buttonContainer}>
              <SubmitButton text={t('auth.login.submit')} small />
            </div>
          )}
        </Form>
      </Formik>
      <LoginViaCodeModal
        email={email}
        isOpen={!!email && method === 'EMAIL'}
        onRequestClose={() => {
          setMethod(undefined);
        }}
        onSuccess={onLoginSuccess}
      />
    </AnalyticsCategoryProvider>
  );
};
